'use client';

import React, { useCallback, useEffect, useRef } from 'react';
import classNames from 'classnames';

import { searchClient } from 'components/essentials/AlgoliaSearchClient';
import { InstantSearch, Configure, Index } from 'react-instantsearch';

import {
  ALGOLIA_INDEX_NAME,
  DEFAULT_SEARCH_MODAL_NUMERIC_FILTERS,
} from 'commons/constants';
import useOpenSearch from 'global-state/isSearchOpen/useOpenSearch';
import useIsSearchOpen from 'global-state/isSearchOpen/useIsSearchOpen';
import useCloseSearch from 'global-state/isSearchOpen/useCloseSearch';

import useAdvancedSearchURL from 'components/consumer/consumer-search/advanced-search-next/hooks/useAdvancedSearchURL';
import useIsAdvancedSearch from 'components/consumer/consumer-search/hooks/useIsAdvancedSearch';

import JBSearchBoxNext from 'components/consumer/consumer-search/consumer-search-modal-next/components/JBSearchBoxNext';
import SearchResultsWrapperNext from 'components/consumer/consumer-search/consumer-search-modal-next/components/SearchResultsWrapperNext';
import CategoryHitsNext from 'components/consumer/consumer-search/consumer-search-modal-next/components/CategoryHitsNext';
import ClearModalSearchQueryWhenClosed from 'components/consumer/consumer-search/consumer-search-modal-next/components/ClearModalSearchQueryWhenClosed';

import SearchModalProductListMobileNext from './components/SearchModalProductListMobileNext';
import StaticPageHitsNext from './components/StaticPageHitsNext';
import ResultsByCategoryNextMobile from './components/ResultsByCategoryNextMobile';

/**
 * Consumer Search Bar - Next version (for new Consumer app)
 * Uses React InstantSearch v7
 */

const InstantSearchMobileNext = ({ toggleMobileSearchBar }) => {
  const prevPathnameRef = useRef(null);

  const closeSearch = useCloseSearch();
  const openSearch = useOpenSearch();
  const isSearchOpen = useIsSearchOpen();
  const isAdvancedSearch = useIsAdvancedSearch();
  const { pathname } = useAdvancedSearchURL();

  useEffect(() => {
    if (
      prevPathnameRef.current !== null &&
      prevPathnameRef.current !== pathname
    ) {
      closeSearch();
    }
    prevPathnameRef.current = pathname;
  }, [pathname, closeSearch]);

  const handleSearchBarFocus = useCallback(() => {
    openSearch();
  }, [openSearch]);

  return (
    <div
      className={classNames(
        'bg-white max-[1023px]:relative max-[1023px]:z-10',
        {
          hidden: isAdvancedSearch,
        }
      )}
    >
      {/* Search Modal */}
      <div className="bg-white relative max-[1023px]:z-10 lg:!z-[1002]">
        <InstantSearch
          searchClient={searchClient}
          indexName={ALGOLIA_INDEX_NAME.PRODUCTS}
        >
          <Configure
            hitsPerPage={3}
            facets={['*']}
            numericFilters={DEFAULT_SEARCH_MODAL_NUMERIC_FILTERS}
            attributesToRetrieve={['_highlightResult', 'product_id', 'name']}
          />

          <ClearModalSearchQueryWhenClosed />

          <JBSearchBoxNext
            autoFocus
            isActive
            setInactive={() => {}}
            toggleSearchOpen={closeSearch}
            variant="InstantSearchMobile"
            handleSearchBarFocus={handleSearchBarFocus}
            toggleMobileSearchBar={toggleMobileSearchBar}
          />
          {isSearchOpen && (
            <SearchResultsWrapperNext>
              <div
                className="bg-white absolute w-full z-[1000] pb-36 max-h-[calc(100dvh-7.5rem)] overflow-y-auto overflow-x-hidden'"
                data-xc="search-dropdown-preview"
              >
                <div className="flex flex-col w-full max-w-none mx-auto px-2 gap-6">
                  <div className="">
                    <div
                      className="bg-gray-50 p-0 rounded-lg h-full"
                      data-xc="search-dropdown-categories"
                    >
                      <Index indexName="categories">
                        <Configure
                          hitsPerPage={5}
                          numericFilters={['fk_store_id=1']}
                          facetFilters={['is_searchable:1']}
                          attributesToRetrieve={[
                            'objectID',
                            'category_name',
                            'category_sef_url',
                            'url',
                          ]}
                        />
                        <CategoryHitsNext
                          toggleSearchOpen={closeSearch}
                          variant="mobile"
                        />
                      </Index>
                    </div>
                  </div>

                  <div className="">
                    <div
                      className="bg-gray-50 rounded-lg"
                      data-xc="search-dropdown-products"
                    >
                      <SearchModalProductListMobileNext
                        toggleSearchOpen={closeSearch}
                      />
                    </div>
                  </div>

                  <div className="min-w-[20%] flex-[0_1_20%] h-full p-0 instant-search-form__each-results-container-non-padded">
                    <Index indexName={ALGOLIA_INDEX_NAME.STATIC_PAGES}>
                      <Configure
                        hitsPerPage={3}
                        numericFilters={[
                          'is_active=1',
                          'show_in_search=1',
                          'fk_store_id=1',
                        ]}
                        attributesToRetrieve={['objectID', 'page', 'url']}
                      />
                      <StaticPageHitsNext
                        toggleSearchOpen={closeSearch}
                        variant="mobile"
                      />
                    </Index>
                    <div className="[border-right:1px_solid_#f1f1f1] pt-5 pr-8 pb-10 p-0 min-h-[auto] mb-2 w-full border-0 order-[-1]">
                      <ResultsByCategoryNextMobile
                        toggleSearchOpen={closeSearch}
                        variant="mobile"
                      />
                    </div>
                  </div>
                </div>
              </div>
              <Index indexName={ALGOLIA_INDEX_NAME.REVIEWS}>
                <Configure facets={['*']} numericFilters="" facetFilters="" />
              </Index>
              <Index indexName={ALGOLIA_INDEX_NAME.CUSTOMER_PHOTOS}>
                <Configure facets={['*']} numericFilters="" />
              </Index>
            </SearchResultsWrapperNext>
          )}
        </InstantSearch>
      </div>
    </div>
  );
};

export default InstantSearchMobileNext;
